import { eventChannel } from 'redux-saga';
import { call } from 'redux-saga/effects';
import firebase from '../config/firebase.config';
import appConfig from '../config/constants';
import { AES, enc } from 'crypto-js';

const auth = firebase.auth();
const database = firebase.firestore();
let _authChannel = null;

function authChannel() {
  if (_authChannel) return _authChannel;
  const channel = eventChannel((emit) => {
    const unsubscribe = auth.onAuthStateChanged(
      (user) => emit({ user }),
      (error) => emit({ error })
    );
    return unsubscribe;
  });

  _authChannel = channel;
  return channel;
}

function* signInWithEmail(email, password) {
  const response = yield call(
    [auth, auth.signInWithEmailAndPassword],
    email,
    password
  );
  if (response) {
    try {
      const cipherText = AES.encrypt(
        '298a5942-0066-11ee-be56-0242ac120002',
        '9a002f5a-7ea6-403f-b2b7-d50401f40fd6'
      );
      response.user['token'] = cipherText.toString();
      database
        .collection('membersDatabase')
        .where('accountEmailid', '==', email)
        .get()
        .then((collections) => {
          const auto = collections.docs.map((res) => res.id);
          try {
            database.collection('membersDatabase').doc(auto[0]).update({
              token: cipherText.toString(),
            });
            localStorage.setItem('userToken', cipherText.toString());
            localStorage.setItem('activeUserId', response?.user?.uid);
          } catch (error) {
            console.log(error);
          }
        });
    } catch (error) {
      console.log(error);
    }
  }
  return response;
}

/**
 * SignUpWithEmailAndPassword - Create new user
 * @param {*} email
 * @param {*} password
 * @returns
 */
function* signUpWithEmailAndPassword(email, password) {
  return yield call(
    [auth, auth.createUserWithEmailAndPassword],
    email,
    password
  );
}

/**
 * SendVerificationEmail - Sends verification email
 */

async function sendVerificationEmail(data) {
  try {
    const functions = firebase.functions();
    const sendVerificationEmail = functions.httpsCallable(
      'sendVerificationEmail'
    );
    const result = await sendVerificationEmail(data);
    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error('Error sending verification email:', error);
    return {
      success: false,
      data: error.message || 'Failed to send verification email',
    };
  }
}

// Checks the verification link and gets back a token to sign in with,
// even if the link was opened on a different device than signup.
async function confirmEmailVerification(data) {
  try {
    const functions = firebase.functions();
    const confirmEmailVerification = functions.httpsCallable(
      'confirmEmailVerification'
    );
    const result = await confirmEmailVerification(data);
    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error('Error confirming email verification:', error);
    return {
      success: false,
      data: error.message || 'Failed to verify email',
    };
  }
}

// Signs in using the token from confirmEmailVerification.
async function signInWithCustomToken(token) {
  try {
    const response = await auth.signInWithCustomToken(token);
    return { success: true, data: response };
  } catch (error) {
    console.error('Error signing in with custom token:', error);
    return {
      success: false,
      data: error.message || 'Failed to sign in',
    };
  }
}

// Lets an invited employee skip the verify-email step, since the invite
// itself already proves they own the address.
async function verifyInviteSignupEmail(data) {
  try {
    const functions = firebase.functions();
    const verifyInviteSignupEmail = functions.httpsCallable(
      'verifyInviteSignupEmail'
    );
    const result = await verifyInviteSignupEmail(data);
    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error('Error verifying invite signup email:', error);
    return {
      success: false,
      data: error.message || 'Failed to verify invite',
    };
  }
}

/**
 * Update the logged-in user's name across all stores in this order:
 * 1) Firebase Auth displayName
 * 2) membersDatabase.accountFullName
 * 3) teamDatabase.memberDetails.{uid}.userName for all teamList entries
 *    across all companies (including deleted teamList rows)
 */
async function updateUserNameEverywhere(name) {
  try {
    const trimmedName = String(name ?? '').trim();
    if (!trimmedName) {
      return { success: false, data: 'Name cannot be empty' };
    }

    const user = auth.currentUser;
    if (!user) {
      return { success: false, data: 'No authenticated user found' };
    }

    const uid = user.uid;
    if (!uid) {
      return { success: false, data: 'Invalid authenticated user' };
    }

    // 1) Firebase Auth
    await user.updateProfile({ displayName: trimmedName });

    const memberRef = database.collection('membersDatabase').doc(uid);

    // 2) membersDatabase
    await memberRef.set(
      {
        accountFullName: trimmedName,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true }
    );

    // 3) teamDatabase roster userName for all teamList rows (including deleted)
    // Client rows excluded: they must never land in teamDatabase.memberDetails.
    const companyListSnapshot = await memberRef.collection('companyList').get();
    const teamIds = new Set();

    await Promise.all(
      companyListSnapshot.docs.map(async (companyDoc) => {
        const teamListSnapshot = await companyDoc.ref
          .collection('teamList')
          .get();
        teamListSnapshot.docs.forEach((teamDoc) => {
          if (teamDoc.id && teamDoc.data()?.Role !== 'Client') {
            teamIds.add(teamDoc.id);
          }
        });
      })
    );

    const MAX_BATCH_OPS = 450;
    let batch = database.batch();
    let opCount = 0;
    let updatedTeams = 0;

    for (const teamId of teamIds) {
      const teamRef = database.collection('teamDatabase').doc(teamId);
      const teamSnapshot = await teamRef.get();
      if (!teamSnapshot.exists) {
        continue;
      }

      batch.update(teamRef, {
        [`memberDetails.${uid}.userName`]: trimmedName,
      });
      opCount += 1;
      updatedTeams += 1;

      if (opCount >= MAX_BATCH_OPS) {
        await batch.commit();
        batch = database.batch();
        opCount = 0;
      }
    }

    if (opCount > 0) {
      await batch.commit();
    }

    return {
      success: true,
      data: {
        message: 'Name updated successfully',
        updatedTeams,
      },
    };
  } catch (e) {
    console.error('Error updating user name everywhere:', e);
    return {
      success: false,
      data: e.message || 'Failed to update user name',
    };
  }
}

function* updateProfileName(name) {
  try {
    const user = auth.currentUser;
    if (!user) {
      return yield { success: false, data: 'No authenticated user found' };
    }
    return yield user
      .updateProfile({ displayName: name })
      .then(() => ({ success: true, data: 'Profile updated!' }));
  } catch (e) {
    console.error('Error updating profile name:', e);
    return yield {
      success: false,
      data: e.message || 'Failed to update profile',
    };
  }
}

/**
 * CreateUser - Creates user in `membersDatabase` & in
 * @param {*} param0
 * @returns
 */
async function createUser({ email, uid, fullName, invitationData }) {
  try {
    // Check if user already exists in Firestore
    const existingUser = await database
      .collection('membersDatabase')
      .doc(uid)
      .get();
    if (existingUser.exists) {
      return { success: true, data: 'User already exists' };
    }

    let onboardingCompleted = false;
    if (
      typeof invitationData?.invitedAs === 'string' &&
      invitationData?.invitedAs !== 'Admin'
    ) {
      onboardingCompleted = true;
    }

    // Declare Member data
    const memberInfo = {
      accountEmailid: email,
      accountFullName: fullName,
      desktopLogin: false,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      onboardingData: {
        onboardingCompleted,
        onboardingSteps: {},
      },
    };
    const batch = database.batch();

    let memberDBRef = database.collection('membersDatabase').doc(uid);

    batch.set(memberDBRef, { ...memberInfo }, { merge: true });

    await batch.commit();
    return { success: true, data: 'User created.' };
  } catch (e) {
    console.error('Error creating user:', e);
    return {
      success: false,
      data: e.message || e,
    };
  }
}

/**
 * DeleteUser - Deletes user from Firebase Auth
 * @param {*} uid
 * @returns
 */
async function deleteUser(uid) {
  try {
    const user = auth.currentUser;
    if (user && user.uid === uid) {
      await user.delete();
      return { success: true, data: 'User deleted' };
    }
    return { success: false, data: 'User not found or not authorized' };
  } catch (error) {
    return { success: false, data: error.message };
  }
}

/**
 * DeleteUserDocument - Deletes user document from Firestore
 * @param {*} uid
 * @returns
 */
async function deleteUserDocument(uid) {
  try {
    const userDocRef = database.collection('membersDatabase').doc(uid);
    const userDoc = await userDocRef.get();

    if (userDoc.exists) {
      await userDocRef.delete();
      return { success: true, data: 'User document deleted from Firestore' };
    }
    return { success: true, data: 'User document does not exist in Firestore' };
  } catch (error) {
    console.error('Error deleting user document:', error);
    return { success: false, data: error.message };
  }
}

/**
 * CleanupOrphanedUser - Cleans up orphaned Firebase Auth users
 * @param {*} uid
 * @returns
 */
async function cleanupOrphanedUser(uid) {
  try {
    // Check if user exists in Firestore
    const userDoc = await database.collection('membersDatabase').doc(uid).get();

    if (!userDoc.exists) {
      // User doesn't exist in Firestore, delete from Firebase Auth
      const user = auth.currentUser;
      if (user && user.uid === uid) {
        await user.delete();
        return { success: true, data: 'Orphaned user cleaned up' };
      }
    }
    return { success: true, data: 'No cleanup needed' };
  } catch (error) {
    return { success: false, data: error.message };
  }
}

function* signOut() {
  return yield call([auth, auth.signOut]);
}

function* onIdTokenChanged() {
  return yield call([auth, auth.onIdTokenChanged]);
}

function* signInAnonymously() {
  return yield call([auth, auth.signInAnonymously]);
}

function getIdToken(credential) {
  return auth.currentUser
    .getIdToken(credential)
    .then((success) => success)
    .catch((error) => error);
}

async function currentAccount() {
  let userLoaded = false;
  function getCurrentUser() {
    return new Promise((resolve, reject) => {
      if (userLoaded) {
        resolve(firebase.auth.currentUser);
      }
      const unsubscribe = auth.onAuthStateChanged((user) => {
        userLoaded = true;
        unsubscribe();
        resolve(user);
      }, reject);
    });
  }
  return getCurrentUser();
}

// Refreshes the cached user so emailVerified picks up a backend change —
// it won't update on its own otherwise.
async function reloadCurrentUser() {
  try {
    if (auth.currentUser) {
      await auth.currentUser.reload();
    }
    return { success: true };
  } catch (error) {
    console.error('Error reloading current user:', error);
    return { success: false, data: error.message || 'Failed to reload user' };
  }
}

/**
 * getCurrentUserDetails - Gets details of logged in user
 * @param {*} userId
 * @returns
 */
async function getCurrentUserDetails(userId) {
  try {
    const query = await database.collection('membersDatabase').doc(userId);
    return query.get().then(async (qSnapshot) => {
      if (qSnapshot.exists) {
        let companyList = [];
        await qSnapshot.ref
          .collection('companyList')
          .get()
          .then((companyLSnapshot) => {
            if (!companyLSnapshot.empty) {
              companyLSnapshot.forEach((sCompany) => {
                const companyData = sCompany.data();
                // Exclude soft-removed companies from auth profile payload.
                if (companyData?.deleted === true) return;
                companyList.push(companyData);
              });
            }
          });
        return { success: true, data: { ...qSnapshot.data(), companyList } };
      } else {
        return { success: true, data: {} };
      }
    });
  } catch (e) {
    return { success: false, data: e };
  }
}

async function sendPasswordResetEmail(data) {
  try {
    const functions = firebase.functions();
    const sendPasswordResetEmail = functions.httpsCallable(
      'sendPasswordResetEmail'
    );
    const result = await sendPasswordResetEmail(data);
    if (result?.data?.success) {
      return { success: true, data: 'Password reset email sent' };
    } else {
      return { success: false, data: result?.data?.data };
    }
  } catch (error) {
    return { success: false, data: error.message };
  }
}

/**
 * createLicenseForVerifiedUser - Creates license for user after email verification
 * Calls Cloud Function to generate and assign license
 * @returns {Promise<Object>} Result with success status
 */
async function createLicenseForVerifiedUser() {
  try {
    // Use Firebase Functions httpsCallable (now that service worker exists)
    const functions = firebase.functions();
    const createLicense = functions.httpsCallable(
      'createLicenseForVerifiedUser'
    );

    // Call the function
    const result = await createLicense();

    return {
      success: true,
      data: result.data,
    };
  } catch (error) {
    console.error('Error creating license:', error);

    // Extract error message
    let errorMessage = 'Failed to create license';
    if (error.message) {
      errorMessage = error.message;
    } else if (error.code) {
      errorMessage = `Error ${error.code}: ${error.message || 'Unknown error'}`;
    }

    return {
      success: false,
      data: errorMessage,
    };
  }
}

export default {
  signInWithEmail,
  signInAnonymously,
  signUpWithEmailAndPassword,
  sendVerificationEmail,
  confirmEmailVerification,
  signInWithCustomToken,
  verifyInviteSignupEmail,
  updateUserNameEverywhere,
  updateProfileName,
  createUser,
  deleteUser,
  deleteUserDocument,
  cleanupOrphanedUser,
  onIdTokenChanged,
  getIdToken,
  signOut,
  authChannel,
  getCurrentUserDetails,
  currentAccount,
  reloadCurrentUser,
  sendPasswordResetEmail,
  createLicenseForVerifiedUser,
};
